import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";
import { BRAND } from "@/data/site";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialRole = params.get("role") === "contractor" ? "contractor" : "customer";

  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", postcode: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await register({ ...form, role });
      if (u.role === "customer") navigate("/me/bookings");
      else navigate("/contractor/onboarding");
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="register-page" className="min-h-screen flex items-center justify-center px-5 py-20">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center gap-3 mb-6 justify-center">
          <img src={BRAND.logoUrl} alt="" className="h-14 w-14 object-contain"/>
          <div>
            <div className="font-display uppercase text-xl text-[#F0F4F1] tracking-wider">Green Connect</div>
            <div className="font-display uppercase text-[10px] tracking-[0.3em] text-[#8DE038]">Create account</div>
          </div>
        </Link>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <button data-testid="role-customer" onClick={() => setRole("customer")} className={`p-4 border font-display uppercase tracking-widest text-xs ${role === "customer" ? "border-[#8DE038] bg-[#8DE038]/10 text-[#8DE038]" : "border-[#1F3325] text-[#9CAEA3]"}`}>
            I'm a Customer
          </button>
          <button data-testid="role-contractor" onClick={() => setRole("contractor")} className={`p-4 border font-display uppercase tracking-widest text-xs ${role === "contractor" ? "border-[#8DE038] bg-[#8DE038]/10 text-[#8DE038]" : "border-[#1F3325] text-[#9CAEA3]"}`}>
            I'm a Contractor
          </button>
        </div>

        <form onSubmit={submit} className="border border-[#1F3325] bg-[#0F1712] p-8 md:p-10 space-y-5" data-testid="register-form">
          <h1 className="font-display uppercase text-3xl tracking-tighter text-[#F0F4F1] heading-accent">
            {role === "customer" ? "Find a local pro" : "Earn with Green Connect"}
          </h1>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Full name</label>
              <input data-testid="reg-name" required className="gl-input mt-2" value={form.name} onChange={(e)=>update("name", e.target.value)}/>
            </div>
            <div>
              <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Phone</label>
              <input data-testid="reg-phone" className="gl-input mt-2" value={form.phone} onChange={(e)=>update("phone", e.target.value)} placeholder="07000 000000"/>
            </div>
          </div>
          <div>
            <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Email</label>
            <input data-testid="reg-email" required type="email" className="gl-input mt-2" value={form.email} onChange={(e)=>update("email", e.target.value)}/>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Password (6+ chars)</label>
              <input data-testid="reg-password" required type="password" minLength={6} className="gl-input mt-2" value={form.password} onChange={(e)=>update("password", e.target.value)}/>
            </div>
            <div>
              <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Postcode {role === "contractor" ? "(your base)" : ""}</label>
              <input data-testid="reg-postcode" className="gl-input mt-2" value={form.postcode} onChange={(e)=>update("postcode", e.target.value)} placeholder="LS1 1AA"/>
            </div>
          </div>
          {error && <div data-testid="register-error" className="text-red-400 text-sm border border-red-500/40 bg-red-900/20 p-3">{error}</div>}
          <button data-testid="register-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18}/> : `Create ${role} account`}
          </button>
          <div className="text-center text-sm text-[#9CAEA3]">
            Already have an account? <Link to="/login" className="text-[#8DE038] font-display uppercase tracking-widest text-xs">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
